/**
 * `buildGiftRowFromEvent` — event-as-row builder for the `useGetGifts`
 * live gift list.
 *
 * The new contract's `morfi.v1.GiftEvent` (after the proto extension
 * shipped in Phase 3) carries every field a list row needs:
 *   gift_pda, opaque_id, asset_kind, recipient_handle, recipient_platform,
 *   sender_handle, message, amount_d6, ts, kind, direction
 *
 * So this helper is now a pure projection — no placeholder strings, no
 * refetch dependency for visible fields. The only field not on the wire
 * is the joined counterparty profile (`sender` / `recipient` user
 * identity object); the next staleTime tick refills it from REST.
 *
 * Platform-agnostic — pure data, no web/RN API. Safe in core.
 */

const GIFT_EVENT_KIND_CLAIMED = 2 as const;
const GIFT_DIRECTION_SENT = 1 as const;
const GIFT_DIRECTION_RECEIVED = 2 as const;

const GIFT_KIND_BONDING_CURVE = 1 as const;
const GIFT_KIND_PRIZE_POOL = 2 as const;
const GIFT_KIND_USDC = 3 as const;

interface BuiltGiftCounterparty {
  readonly userId: string;
  readonly displayName: string | null;
  readonly handle: string | null;
  readonly avatarUrl: string | null;
}

export interface BuiltGiftRow {
  readonly giftPda: string;
  readonly opaqueId: string;
  readonly recipientPlatform: 'morfi' | 'x';
  readonly recipientHandle: string;
  readonly assetKind: 'usdc' | 'outcome_token' | 'prize_pool_share' | 'unspecified';
  readonly mintPubkey: string;
  readonly amount: string;
  readonly message: string;
  readonly anonymous: boolean;
  readonly status: 'pending' | 'claimed';
  readonly createdAt: string;
  readonly statusChangedAt: string;
  readonly senderHandle: string | null;
  readonly sender: BuiltGiftCounterparty | null;
  readonly recipient: BuiltGiftCounterparty | null;
}

/** Sentinel returned when the helper drops an event (direction mismatches
 *  the cache's `kind`). The codegen-emitted prepend branch reads
 *  `_newRow[rowKey]` — when it's the empty string, it bails with
 *  `return prev`, leaving the cache untouched. */
const EMPTY_BUILT_GIFT_ROW: BuiltGiftRow = {
  giftPda: '',
  opaqueId: '',
  recipientPlatform: 'morfi',
  recipientHandle: '',
  assetKind: 'unspecified',
  mintPubkey: '',
  amount: '0',
  message: '',
  anonymous: false,
  status: 'pending',
  createdAt: '',
  statusChangedAt: '',
  senderHandle: null,
  sender: null,
  recipient: null,
};

function d6String(v: unknown): string {
  if (typeof v === 'bigint') return v.toString();
  if (typeof v === 'number' && Number.isFinite(v)) return String(Math.trunc(v));
  if (typeof v === 'string' && /^-?\d+$/.test(v.trim())) return v.trim();
  return '0';
}

function readField(raw: Record<string, unknown>, key: string): unknown {
  const nested = raw.payload;
  if (nested !== null && typeof nested === 'object' && key in (nested as object)) {
    return (nested as Record<string, unknown>)[key];
  }
  return raw[key];
}

function readString(raw: Record<string, unknown>, key: string): string {
  const v = readField(raw, key);
  return typeof v === 'string' ? v : '';
}

/**
 * Map the wire enum tag to a kind string.
 *
 * `unspecified` is propagated as-is (NOT silently mapped to a default).
 * The wire payload arrives with `asset_kind = UNSPECIFIED` whenever the
 * indexer's App-PG lookup for the gifts row returns None — that's a
 * graceful-degrade signal, not a real kind. The previous default
 * branch silently mapped UNSPECIFIED → 'outcome_token', mislabeling
 * USDC claims. Consumers should treat 'unspecified' as "kind not yet
 * known; wait for REST refetch to resolve it".
 */
function mapAssetKind(kind: number): BuiltGiftRow['assetKind'] {
  switch (kind) {
    case GIFT_KIND_USDC:
      return 'usdc';
    case GIFT_KIND_PRIZE_POOL:
      return 'prize_pool_share';
    case GIFT_KIND_BONDING_CURVE:
      return 'outcome_token';
    default:
      return 'unspecified';
  }
}

function mapRecipientPlatform(p: string): 'morfi' | 'x' {
  return p === 'x' ? 'x' : 'morfi';
}

function buildSenderCounterparty(senderHandle: string): BuiltGiftCounterparty | null {
  if (senderHandle.length === 0) return null;
  return {
    userId: '',
    displayName: null,
    handle: senderHandle,
    avatarUrl: null,
  };
}

/**
 * Symmetric to `buildSenderCounterparty` — synthesise a slim
 * counterparty object from the wire's `recipientHandle`. Used on Sent
 * rows so the UI can render the counterparty avatar/name immediately
 * from the realtime event, instead of waiting for the next REST
 * refetch to repaint with the joined identity.
 */
function buildRecipientCounterparty(recipientHandle: string): BuiltGiftCounterparty | null {
  if (recipientHandle.length === 0) return null;
  return {
    userId: '',
    displayName: null,
    handle: recipientHandle,
    avatarUrl: null,
  };
}

export function buildGiftRowFromEvent(
  raw: Record<string, unknown>,
  _subject: string,
  args?: Record<string, unknown>,
): BuiltGiftRow {
  const giftPda = readString(raw, 'giftPda');
  const opaqueId = readString(raw, 'opaqueId');
  const mint = readString(raw, 'mint');
  const recipientHandle = readString(raw, 'recipientHandle');
  const recipientPlatform = mapRecipientPlatform(readString(raw, 'recipientPlatform'));
  const senderHandle = readString(raw, 'senderHandle');
  const message = readString(raw, 'message');

  const kindNum = Number(readField(raw, 'kind') ?? 0);
  const assetKindNum = Number(readField(raw, 'assetKind') ?? 0);
  const directionNum = Number(readField(raw, 'direction') ?? 0);

  // Direction filter — the indexer dual-publishes every GiftCreated /
  // GiftClaimed event to `morfi.user.{sender}.gift.v0` (direction=SENT)
  // AND `morfi.user.{recipient}.gift.v0` (direction=RECEIVED). Both of
  // a user's `useGetGifts({kind:'sent'})` + `useGetGifts({kind:'received'})`
  // cache entries subscribe to the SAME subject (their own user's gift
  // subject), so without filtering the prepend recipe would put every
  // event into BOTH lists — leading to a sender seeing their outgoing
  // gift land in their Received tab. Drop the row (empty giftPda → the
  // codegen-emitted "return prev" path) when direction and the hook's
  // `kind` arg disagree.
  const argKind = typeof args?.kind === 'string' ? args.kind : undefined;
  if (argKind === 'sent' && directionNum !== GIFT_DIRECTION_SENT) {
    return EMPTY_BUILT_GIFT_ROW;
  }
  if (argKind === 'received' && directionNum !== GIFT_DIRECTION_RECEIVED) {
    return EMPTY_BUILT_GIFT_ROW;
  }

  const status: 'pending' | 'claimed' = kindNum === GIFT_EVENT_KIND_CLAIMED ? 'claimed' : 'pending';

  const tsNum = Number(readField(raw, 'ts') ?? 0);
  const ms = tsNum > 1e12 ? tsNum : tsNum * 1000;
  const createdAt = new Date(Number.isFinite(ms) && ms > 0 ? ms : Date.now()).toISOString();

  // Direction tells us whether this row will land on the recipient's
  // "Received" inbox or the sender's "Sent" tab. Helps the consumer
  // know which counterparty side to render.
  const isReceived = directionNum === GIFT_DIRECTION_RECEIVED;
  const isSent = directionNum === GIFT_DIRECTION_SENT;

  return {
    giftPda,
    opaqueId,
    recipientPlatform,
    recipientHandle,
    assetKind: mapAssetKind(assetKindNum),
    mintPubkey: mint,
    amount: d6String(readField(raw, 'amountD6')),
    message,
    // Anonymous flag isn't on the wire — empty senderHandle is the
    // signal (the indexer scrubs the handle when anonymous=true).
    // Applies to both directions: a sender who marked their own gift
    // anonymous still sees `anonymous=true` on the Sent row so any UI
    // conditional based on the flag (e.g. "shown as anonymous on the
    // permalink") is correct.
    anonymous: senderHandle.length === 0,
    status,
    createdAt,
    statusChangedAt: status === 'claimed' ? createdAt : createdAt,
    senderHandle: senderHandle.length > 0 ? senderHandle : null,
    sender: isReceived ? buildSenderCounterparty(senderHandle) : null,
    recipient: isSent ? buildRecipientCounterparty(recipientHandle) : null,
  };
}
